// composables/useRealtimeChannel.ts
import { onUnmounted } from 'vue'
import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js'

export function useRealtimeChannel(
  client: SupabaseClient, 
  table: string, 
  schema: string = 'public', 
  options: {
    onInsert?: (record: any) => void,
    onUpdate?: (record: any, oldRecord?: any) => void,
    onDelete?: (record: any) => void,
    maxRetries?: number,
    baseRetryDelay?: number
  }, 
  unsubscribeOnUnmount: boolean = true
) {
    
    let realtimeChannel: RealtimeChannel | null = null
    let retryCount = 0
    let retryTimeout: ReturnType<typeof setTimeout> | null = null
    const maxRetries = options.maxRetries ?? 3
    const baseRetryDelay = options.baseRetryDelay ?? 2000

    const handleEvent = (payload: any) => {
        const { eventType, new: newRecord, old: oldRecord } = payload
        
        
        if (eventType === 'INSERT' && options.onInsert) {
            options.onInsert(newRecord)
        } else if (eventType === 'UPDATE' && options.onUpdate) {
            options.onUpdate(newRecord, oldRecord)
        } else if (eventType === 'DELETE' && options.onDelete) {
            options.onDelete(oldRecord)
        }
    }

    const clearRetryTimeout = () => {
        if (retryTimeout) {
            clearTimeout(retryTimeout)
            retryTimeout = null
        }
    }

    const scheduleRetry = (reason: string) => {
        if (retryCount >= maxRetries) {
            console.error(`❌ Max retries (${maxRetries}) reached for ${schema}.${table}. Stopping retry attempts.`)
            return
        }

        retryCount++
        const delay = baseRetryDelay * Math.pow(2, retryCount - 1) // Exponential backoff
        
        
        clearRetryTimeout()
        retryTimeout = setTimeout(() => {
            
            unsubscribe()
            subscribe()
        }, delay)
    }

    const subscribe = () => {
        if (realtimeChannel) {
            console.warn('Channel already subscribed, unsubscribing first')
            unsubscribe()
        }

        // Create a unique channel name to avoid conflicts
        const channelName = `realtime:${schema}:${table}:${Date.now()}`
        
        
        realtimeChannel = client.channel(channelName)
          .on('postgres_changes', { 
            event: '*', 
            schema, 
            table 
          }, handleEvent)

        if (realtimeChannel) {
            realtimeChannel.subscribe((status: string) => {
                
                
                if (status === 'SUBSCRIBED') {
                    
                    retryCount = 0 // Reset retry count on successful connection
                    clearRetryTimeout()
                } else if (status === 'CHANNEL_ERROR') {
                    console.error(`❌ Channel error for ${schema}.${table}`)
                    scheduleRetry('CHANNEL_ERROR')
                } else if (status === 'TIMED_OUT') {
                    console.error(`⏰ Subscription timed out for ${schema}.${table}`)
                    scheduleRetry('TIMED_OUT')
                } else if (status === 'CLOSED') {
                    
                    // Don't retry on manual close
                }
            })
        }
    }

    const unsubscribe = () => {
        clearRetryTimeout()
        
        if (realtimeChannel) {
            
            try {
                client.removeChannel(realtimeChannel)
            } catch (error) {
                console.error('Error removing channel:', error)
            }
            realtimeChannel = null
        }
    }

    const resetRetries = () => {
        retryCount = 0
        clearRetryTimeout()
    }

    // Auto-unsubscribe on component unmount if in Vue context
    if (unsubscribeOnUnmount) {
        try {
            onUnmounted(() => {
                unsubscribe()
            })
        } catch (error) {
            // Not in Vue context, ignore
        }
    }

    return {
        subscribe,
        unsubscribe,
        resetRetries,
        isSubscribed: () => !!realtimeChannel,
        getRetryCount: () => retryCount
    }
}